import React, { useEffect, useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';

import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMAReportesRelatorios } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import api from '../../services/api';
import AssinaturaComNomesAdicionais, {
	parseNomesEnvolvidosJson,
	stringifyNomesEnvolvidos,
} from './components/AssinaturaComNomesAdicionais';

interface ReportesRelatoriosModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMAReportesRelatorios) => void;
	dadosReportesRelatorios?: CadernoLPMAReportesRelatorios | null;
}

const defaultValues: CadernoLPMAReportesRelatorios = {
	nomeColaboradorEvento: '',
	dataHoraEvento: null,
	motivoTempestades: false,
	motivoAnimais: false,
	motivoPessoas: false,
	motivoOutros: false,
	motivoOutrosDescricao: '',
	estadioFenologicoEvento: '',
	liberacaoForaLPMA: null,
	liberacaoForaCQB: null,
	informacoesAdicionaisEvento: '',
	medidasCorrecaoEvento: '',
	nomeResponsavelReporte: '',
	cargoResponsavelReporte: '',
	dataReporteCIBio: null,
	classificacaoEvento: '',
	dataReporteAcidente: null,
	oficioCIBioNumero: '',
};

const formatarRadioBooleano = (valor: any): string => {
	if (valor === true || valor === 'true') return 'true';
	if (valor === false || valor === 'false') return 'false';
	return '';
};

const normalizarRadio = (valor: any): boolean | null => {
	if (valor === undefined || valor === null || valor === '') return null;
	if (typeof valor === 'boolean') return valor;
	return valor === 'true';
};

export default function ReportesRelatoriosModal({ show, onHide, onSubmit, dadosReportesRelatorios }: ReportesRelatoriosModalProps) {
	const {
		register,
		handleSubmit,
		control,
		reset,
		getValues,
	} = useForm<CadernoLPMAReportesRelatorios>({
		mode: 'all',
		defaultValues,
	});

	// Estados para assinatura
	const [assinaturaResponsavel, setAssinaturaResponsavel] = useState<string | null>(null);
	const [assinaturaUsuario, setAssinaturaUsuario] = useState<string | null>(null);
	const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
	const [nomesAdicionais, setNomesAdicionais] = useState<string[]>([]);

	// Carregar assinatura do usuário
	useEffect(() => {
		if (show) {
			const carregarAssinatura = async () => {
				try {
					const token = localStorage.getItem('token');
					if (token) {
						const decoded: any = JSON.parse(atob(token.split('.')[1]));
						const username = decoded.unique_name || '';
						
						const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
						if (data && data.assinaturaBase64) {
							setAssinaturaUsuario(data.assinaturaBase64);
						}
					}
				} catch (error) {
					// Usuário não tem assinatura cadastrada
				}
			};
			
			carregarAssinatura();
			
			// Carregar assinatura salva se existir
			if (dadosReportesRelatorios?.assinaturaResponsavelReporteBase64) {
				setAssinaturaResponsavel(dadosReportesRelatorios.assinaturaResponsavelReporteBase64);
			} else {
				// Limpar se não houver assinatura salva
				setAssinaturaResponsavel(null);
			}
			setNomesAdicionais(parseNomesEnvolvidosJson(dadosReportesRelatorios?.nomesEnvolvidosAdicionaisJson));
		} else {
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, dadosReportesRelatorios?.assinaturaResponsavelReporteBase64, dadosReportesRelatorios?.nomesEnvolvidosAdicionaisJson]);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setAssinaturaResponsavel(null);
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
		} else if (dadosReportesRelatorios) {
			const parseDate = (valor: any) => {
				if (!valor) return null;
				const data = new Date(valor);
				return Number.isNaN(data.getTime()) ? null : data;
			};

			reset({
				...dadosReportesRelatorios,
				liberacaoForaLPMA: formatarRadioBooleano(dadosReportesRelatorios.liberacaoForaLPMA),
				liberacaoForaCQB: formatarRadioBooleano(dadosReportesRelatorios.liberacaoForaCQB),
				dataHoraEvento: parseDate(dadosReportesRelatorios.dataHoraEvento),
				dataReporteCIBio: parseDate(dadosReportesRelatorios.dataReporteCIBio),
				dataReporteAcidente: parseDate(dadosReportesRelatorios.dataReporteAcidente),
			});
			setNomesAdicionais(parseNomesEnvolvidosJson(dadosReportesRelatorios.nomesEnvolvidosAdicionaisJson));
		}
	}, [show, dadosReportesRelatorios, reset]);

	const handleSubmitInterno = (dados: CadernoLPMAReportesRelatorios) => {
		const valores = getValues();

		const dadosComAssinatura = {
			...dados,
			liberacaoForaLPMA: normalizarRadio(valores.liberacaoForaLPMA ?? dados.liberacaoForaLPMA),
			liberacaoForaCQB: normalizarRadio(valores.liberacaoForaCQB ?? dados.liberacaoForaCQB),
			classificacaoEvento: valores.classificacaoEvento ?? dados.classificacaoEvento ?? '',
			assinaturaResponsavelReporteBase64: assinaturaResponsavel || null,
			nomesEnvolvidosAdicionaisJson: stringifyNomesEnvolvidos(nomesAdicionais),
		};

		onSubmit(dadosComAssinatura);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.REPORTES_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(handleSubmitInterno)} autoComplete="off">
                
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<strong>1. Objetivo</strong>
						</Form.Label>
						<p>
							Este formulário deve ser utilizado apenas para reporte de eventos caracterizados como incidentes e/ou acidentes
							envolvendo material GM regulamentado pela Lei 11.105 de 24 de março de 2005.
						</p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<strong>2. Atenção</strong>
						</Form.Label>
						<p>
							Entrar em contato imediatamente com a CIBio em caso de identificação de eventos que possam ser caracterizados como incidentes e/ou acidentes envolvendo material GM regulado.
						</p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<strong>3. Informações necessárias sobre o evento</strong>
						</Form.Label>
						<p className="mb-2">
							Caderno de Conformidade de Campo (FCN) 126
						</p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>a. Nome do colaborador que detectou o evento</Form.Label>
						<Form.Control
							ref={register}
							name="nomeColaboradorEvento"
							placeholder="Informe o nome do colaborador"
							maxLength={150}
						/>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom">
					<Col md={6}>
						<Form.Group>
							<Form.Label>b. Data e hora estimada da ocorrência do evento</Form.Label>
							<Controller
								control={control}
								name="dataHoraEvento"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
										showTimeSelect
										timeFormat="HH:mm"
										timeIntervals={30}
										timeCaption="Hora"
									/>
								)}
							/>
						</Form.Group>
					</Col>
				</Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>c. Motivo / causa do evento</Form.Label>
						<div className="d-flex flex-column">
							<Form.Check type="checkbox" ref={register} name="motivoTempestades" label="Tempestades, enxuradas e vendavais" />
							<Form.Check type="checkbox" ref={register} name="motivoAnimais" label="Presença de animais (ataque, destruição, consumo etc.)" />
							<Form.Check type="checkbox" ref={register} name="motivoPessoas" label="Presença de pessoas não autorizadas (invasão, destruição, furto, consumo etc.)" />
							<Form.Check type="checkbox" ref={register} name="motivoOutros" label="Outros (descreva)" />
						</div>
						<Form.Control
							className="mt-2"
							ref={register}
							name="motivoOutrosDescricao"
							placeholder="Detalhe outros motivos, se necessário"
							maxLength={200}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>d. Estádio fenológico da cultura</Form.Label>
						<Form.Control
							ref={register}
							name="estadioFenologicoEvento"
							placeholder="Descreva o estádio fenológico"
							maxLength={200}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>e. Houve liberação de material GM viável para fora da área da LPMA?</Form.Label>
						<Controller
							control={control}
							name="liberacaoForaLPMA"
							render={({ onChange, value }) => (
								<div className="d-flex">
									<div className="form-check mr-3">
										<input
											type="radio"
											className="form-check-input"
											id="liberacaoForaLPMASim"
											checked={value === true || value === 'true'}
											onChange={() => onChange('true')}
										/>
										<label className="form-check-label" htmlFor="liberacaoForaLPMASim">
											Sim
										</label>
									</div>
									<div className="form-check">
										<input
											type="radio"
											className="form-check-input"
											id="liberacaoForaLPMANao"
											checked={value === false || value === 'false'}
											onChange={() => onChange('false')}
										/>
										<label className="form-check-label" htmlFor="liberacaoForaLPMANao">
											Não
										</label>
									</div>
								</div>
							)}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>f. Houve liberação de material GM viável para fora da área com CQB?</Form.Label>
						<Controller
							control={control}
							name="liberacaoForaCQB"
							render={({ onChange, value }) => (
								<div className="d-flex">
									<div className="form-check mr-3">
										<input
											type="radio"
											className="form-check-input"
											id="liberacaoForaCQBSim"
											checked={value === true || value === 'true'}
											onChange={() => onChange('true')}
										/>
										<label className="form-check-label" htmlFor="liberacaoForaCQBSim">
											Sim
										</label>
									</div>
									<div className="form-check">
										<input
											type="radio"
											className="form-check-input"
											id="liberacaoForaCQBNao"
											checked={value === false || value === 'false'}
											onChange={() => onChange('false')}
										/>
										<label className="form-check-label" htmlFor="liberacaoForaCQBNao">
											Não
										</label>
									</div>
								</div>
							)}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>g. Informações adicionais sobre o evento detectado</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="informacoesAdicionaisEvento"
							placeholder="Seja o mais específico possível"
							maxLength={500}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<strong>4. Medidas de correção adotadas</strong>
						</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="medidasCorrecaoEvento"
							placeholder="Descreva as ações corretivas"
							maxLength={500}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<strong>5. Responsável pelo reporte do evento para CIBio</strong>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Nome do responsável</Form.Label>
						<Form.Control
							ref={register}
							name="nomeResponsavelReporte"
							placeholder="Insira o nome do responsável"
							maxLength={150}
						/>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom">
					<Col md={6}>
						<Form.Group>
							<Form.Label>Cargo</Form.Label>
							<Form.Control
								ref={register}
								name="cargoResponsavelReporte"
								placeholder="Cargo"
								maxLength={150}
							/>
						</Form.Group>
					</Col>
					<Col md={6}>
						<Form.Group>
							<Form.Label>Data do reporte</Form.Label>
							<Controller
								control={control}
								name="dataReporteCIBio"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Form.Group>
					</Col>
				</Row>

				<AssinaturaComNomesAdicionais
					label="Assinatura do Responsável"
					assinaturaUsuario={assinaturaUsuario}
					assinaturaBase64={assinaturaResponsavel}
					onAssinaturaBase64Change={setAssinaturaResponsavel}
					nomesAdicionais={nomesAdicionais}
					onNomesAdicionaisChange={setNomesAdicionais}
					mostrarAssinatura={mostrarAssinatura}
					onMostrarAssinaturaChange={setMostrarAssinatura}
				/>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<strong>6. Classificação do evento após investigação e análise pela CIBio</strong>
						</Form.Label>
						<Controller
							control={control}
							name="classificacaoEvento"
							render={({ onChange, value }) => (
								<div className="d-flex flex-column">
									<div className="form-check">
										<input
											type="radio"
											className="form-check-input"
											id="classificacaoEventoIncidente"
											checked={value === 'INCIDENTE'}
											onChange={() => onChange('INCIDENTE')}
										/>
										<label className="form-check-label" htmlFor="classificacaoEventoIncidente">
											Incidente (reporte em relatório conclusivo e anual)
										</label>
									</div>
									<div className="form-check">
										<input
											type="radio"
											className="form-check-input"
											id="classificacaoEventoAcidente"
											checked={value === 'ACIDENTE'}
											onChange={() => onChange('ACIDENTE')}
										/>
										<label className="form-check-label" htmlFor="classificacaoEventoAcidente">
											Acidente (reporte imediato à CTNBio e órgãos/entidades de registro e fiscalização)
										</label>
									</div>
								</div>
							)}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<strong>7. Em caso de acidente</strong>
						</Form.Label>
						<Row>
							<Col md={4}>
								<Form.Group>
									<Form.Label>Data do reporte para CTNBio, MAPA e IBAMA</Form.Label>
									<Controller
										control={control}
										name="dataReporteAcidente"
										render={({ onChange, value }) => (
											<DatePicker
												selected={value || null}
												onChange={onChange}
												className="input-custom-to-date"
												locale="pt-BR"
												dateFormat="dd/MM/yyyy"
												placeholderText="Selecionar a data"
											/>
										)}
									/>
								</Form.Group>
							</Col>
							<Col md={8}>
								<Form.Group>
									<Form.Label>N.º Ofício CIBio</Form.Label>
									<Form.Control
										ref={register}
										name="oficioCIBioNumero"
										placeholder="Informe o número do ofício"
										maxLength={150}
									/>
								</Form.Group>
							</Col>
						</Row>
						<p className="mt-2">
							<small>* Anexar cópia do Ofício CIBio na ABA comunicados externos.</small>
						</p>
					</Form.Group>
				</Form.Row>

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Inserir" contained={true} type="submit" />
				</div>
			</Form>
		</Modal>
	);
}

